/**
 * PUT /timesheets/:id — a worker correcting their OWN shift (api #595).
 *
 * Hand-written, not `ApiResponse<'PUT /api/v1/timesheets/:id'>`: the manifest
 * probe PUTs against an unknown id and records the 404, so the route has no
 * recorded 2xx shape to name. Keys are the handler's literal `c.json({...})`
 * in timesheets.routes.ts and the service's `updateTimesheet`.
 *
 * The body is the exact set of fields `updateTimesheet` reads — snake_case on
 * the wire, as every write is. Since api#1148 that includes `clock_in_at`: the
 * owner may move the tap-IN too, but only to another time on the SAME
 * tenant-local day the shift is filed on (that day chose its pay period), and
 * only to before the clock-out.
 *
 * Who may call it is the SERVICE's predicate, not this type's business:
 * `employee_id = <JWT userId>` and `status IN WORKER_EDITABLE_STATUSES`.
 * Anything outside that is a 404 `Not found or not editable` — 404 rather
 * than 403 so a colleague's row is not confirmed to exist. An office role
 * reaches the same door with the same predicate; office corrections are a
 * different action and this is not it.
 */
export interface TimesheetSelfEditBody {
  /**
   * ISO instant (api#1148). Must parse, come before the clock-out, and fall on
   * the tenant-local day the shift is filed on — else 400 `invalid_clock_in` or
   * `clock_in_changes_day`.
   */
  clock_in_at?: string;
  /** ISO instant. Must parse and must be after the row's clock_in_at. */
  clock_out_at?: string;
  per_diem_amount?: number;
  worker_note?: string;
  pay_code_id?: string;
  work_location?: string;
}

/**
 * Mirrors WORKER_EDITABLE_STATUSES in the API's timesheets.service.ts.
 * `approved` and `locked` are out: approval is the two-party attestation, and
 * a row someone has signed for is not the worker's to move. Used to decide
 * whether to OFFER the edit; the server decides whether to accept it.
 */
export const WORKER_EDITABLE_STATUSES = ['draft', 'rejected', 'submitted'] as const;

export function isWorkerEditableStatus(status: string | null | undefined): boolean {
  return (WORKER_EDITABLE_STATUSES as readonly string[]).includes(status ?? '');
}

export interface TimesheetSelfEditResponse {
  success: true;
  /** The updated row, `SELECT *` shape — raw snake_case. */
  data: Record<string, unknown>;
}

/** The refusal envelopes PUT /timesheets/:id sends. `code` only on the 400s. */
export interface TimesheetSelfEditRefusal {
  success: false;
  error: string;
  code?: 'invalid_clock_out' | 'invalid_work_location' | 'invalid_clock_in' | 'clock_in_changes_day';
}

/**
 * POST /timesheets/manual — the caller's OWN missed shift (api#1148). Any role;
 * `employee_id` is the JWT user, nothing in the body names a worker. The row is
 * created with `clock_source = 'manual'` and submitted, so it waits for someone
 * ELSE to approve it (the owner's approve is a 409 `self_approval`).
 *
 * Hand-written: the manifest probe recorded the 400 for an empty body, so
 * `ApiResponse<'POST /api/v1/timesheets/manual'>` would name an error shape.
 */
export interface ManualTimesheetBody {
  /** The calendar day worked, `YYYY-MM-DD`, in the TENANT's zone. */
  date: string;
  /** ISO instant, on `date` in companies.time_zone. */
  clock_in_at: string;
  /** ISO instant, after the clock-in and already past. May be the next day. */
  clock_out_at: string;
  /** 'shop' | 'field' | null. The route also accepts it as `location`. */
  work_location: string | null;
  /** Required. Stored as the worker's note and shown to the approver. */
  reason: string;
}

export interface ManualTimesheetResponse {
  success: true;
  /** The row (`RETURNING *`) plus whether the hand-off to the queue worked. */
  data: Record<string, unknown> & { id: string; submitted: boolean; submission_error: string | null };
}

/** Every code POST /timesheets/manual can refuse with, and its status. */
export type ManualTimesheetRefusalCode =
  | 'reason_required' | 'invalid_date' | 'invalid_clock_in' | 'invalid_clock_out'
  | 'clock_in_not_on_date' | 'shift_not_ended' | 'invalid_work_location'   // 400
  | 'period_closed' | 'no_pay_period' | 'overlapping_shift';                // 409

export interface ManualTimesheetRefusal {
  success: false;
  error: string;
  code?: ManualTimesheetRefusalCode;
  /** `period_closed`: { periodId, status }. `overlapping_shift`: { timesheetId, status }. */
  details?: Record<string, unknown>;
}

/**
 * One row of GET /audit/timesheet/:id — `SELECT *` from timesheet_audit_log.
 * Hand-written for the same reason as above: the manifest probe recorded the
 * 404 for an unknown id.
 *
 * `changes` is `{ field: { from, to } }`, written by api #595 for every
 * worker-editable field that moved AND for the figures a clock-out edit
 * derives (raw_minutes, standby_minutes, the three hours columns). Older rows
 * (submit / approve / reject) carry `null` here and say what happened through
 * `action` and the two status columns.
 */
export interface TimesheetAuditRow {
  id: string;
  company_id: string;
  timesheet_id: string;
  action: string;
  performed_by: string | null;
  performed_by_role: string | null;
  previous_status: string | null;
  new_status: string | null;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  note: string | null;
  agent_name: string | null;
  created_at: string;
}

/** NO `success` key on this one — audit-log.routes.ts sends `{ data }` bare. */
export interface TimesheetAuditResponse {
  data: TimesheetAuditRow[];
}

/**
 * POST /timesheets/:id/submit — handing a `draft` shift to the approval queue.
 *
 * Hand-written for the same reason as the two above: the manifest probe POSTs
 * against an unknown id and recorded the 404, so
 * `ApiResponse<'POST /api/v1/timesheets/:id/submit'>` is a type built from a
 * 4xx body and naming it would be worse than naming nothing. Keys are the
 * route's literal `c.json({...})` and `submitTimesheet`'s `RETURNING *`.
 *
 * Lives in this file rather than a new one because it is the same route
 * prefix and the same workflow the self-edit types describe — a worker's own
 * shift, before anyone has signed for it.
 */
export interface TimesheetSubmitResponse {
  success: true;
  /** The submitted row, `RETURNING *` shape — raw snake_case. */
  data: Record<string, unknown>;
}

/**
 * POST /timesheets/:id/resubmit — the worker handing a REJECTED shift back
 * (`resubmitTimesheet`). Owner-only and rejected-only; anything else is a bare
 * 404 "Not found or not in rejected status". The row is `RETURNING *`, raw
 * snake_case, with rejection_reason and supervisor_note now NULL.
 */
export interface TimesheetResubmitResponse {
  success: true;
  data: Record<string, unknown>;
}

/**
 * The refusals. `not_yours_to_submit` is the only coded one — a 403 from
 * `submitTimesheet`'s on-behalf branch, which uses the STRICT approver list
 * and so refuses an elevated dispatcher that the approve route would let
 * through. The uncoded case is a bare 404, "Not found or not in draft status",
 * which is also what a second click gets: the UPDATE is predicated on the
 * status it is leaving.
 */
export interface TimesheetSubmitRefusal {
  success: false;
  error: string;
  code?: 'not_yours_to_submit';
}
